import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { SlideShell } from "./SlideShell";
import RecapCollage from "./RecapCollage";
import { playDing, unlockAudio } from "@/lib/synth";
import Staff from "@/components/Staff";
import { buildHebrewSequence } from "@/lib/hebrewComposition";
import { HEBREW_BASE_LETTERS } from "@/lib/hebrewCryptogram";
import { GhostLetter } from "@/components/GhostLetter";

// Act 3 Scene 1 — Hebrew scale used for the staff demo. Matches the
// Live Display's Hebrew mode so the notation is identical to Act 3
// Scene 2's audience takeover.
const HEBREW_SCALE = { root: "C", modeIdx: 0 };
const DEMO_STEP_MS = 300;
const DEMO_GHOST_MS = 3400;
const DEMO_GHOST_PALETTE = ["#A774FF", "#F2D589", "#FCA07A", "#B79CE8", "#7ED9A6", "#FF7EB6"];

function HebrewDemoStaff({ demo }) {
  const dropped = React.useMemo(() => {
    if (!demo) return [];
    const text = demo.kind === "alefbet" ? HEBREW_BASE_LETTERS.join("") : (demo.text || "");
    const words = text.split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    return buildHebrewSequence(words, HEBREW_SCALE);
  }, [demo]);

  const startMs = demo?.started_at ? new Date(demo.started_at).getTime() : null;
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!startMs) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(id);
  }, [startMs]);

  const count = !startMs
    ? 0
    : Math.max(0, Math.min(dropped.length, Math.floor((now - startMs) / DEMO_STEP_MS) + 1));
  const shown = dropped.slice(0, count);

  // Fantasia ghost letters float up off the staff as each note lands —
  // ghosts only, no smoke, on this slide.
  const staffRef = React.useRef(null);
  const [ghosts, setGhosts] = React.useState([]);
  const spawnedRef = React.useRef(0);
  const demoKeyRef = React.useRef(null);
  const ghostCursorRef = React.useRef(0);
  React.useEffect(() => {
    if (!demo) {
      spawnedRef.current = 0;
      demoKeyRef.current = null;
      setGhosts([]);
      return;
    }
    // A new push (different started_at) restarts the reveal from letter
    // one, so the spawn counter has to rewind with it — otherwise only
    // the first word of the session ever gets ghosts.
    if (demoKeyRef.current !== demo.started_at) {
      demoKeyRef.current = demo.started_at;
      spawnedRef.current = 0;
    }
    if (count <= spawnedRef.current) return;
    const rect = staffRef.current?.getBoundingClientRect();
    const fresh = [];
    for (let i = spawnedRef.current; i < count; i += 1) {
      const entry = dropped[i];
      if (!entry) continue;
      const id = `${demo.started_at}-${i}`;
      // Colours keep advancing through the palette across pushes, so the
      // same word never comes up in the same colours twice.
      const color = DEMO_GHOST_PALETTE[ghostCursorRef.current % DEMO_GHOST_PALETTE.length];
      ghostCursorRef.current += 1;
      fresh.push({
        id,
        letter: entry.letter,
        color,
        dur: DEMO_GHOST_MS,
        box: 240,
        cx: (0.5 + (Math.random() - 0.5) * 0.85) * window.innerWidth,
        cy: (rect ? rect.top : window.innerHeight * 0.7) - 6,
        dx: (Math.random() - 0.5) * 30,
        dy: -(30 + Math.random() * 15),
        rotate: (Math.random() - 0.5) * 70,
        seed: Math.floor(Math.random() * 1000),
      });
      window.setTimeout(() => {
        setGhosts((prev) => prev.filter((g) => g.id !== id));
      }, DEMO_GHOST_MS + 200);
    }
    spawnedRef.current = count;
    if (fresh.length) setGhosts((prev) => [...prev, ...fresh]);
  }, [count, demo, dropped]);

  return (
    <div className="mt-10 w-full" data-testid="slide-hebrew-demo" data-revealed={count}>
      {ghosts.map((g) => (
        <GhostLetter key={g.id} ghost={g} />
      ))}
      {demo?.label ? (
        <div
          className="mb-3 text-xs sm:text-sm"
          style={{ color: "#F2D589", letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 600 }}
          data-testid="slide-hebrew-demo-label"
        >
          {demo.label}
        </div>
      ) : null}
      <div ref={staffRef}>
        <Staff droppedEntries={shown} activeIndex={shown.length - 1} fluid reserveEntries={dropped} />
      </div>
    </div>
  );
}


const STOPLIGHTS = [
  { field: "stoplight_red", color: "#1A0F3C", glow: "rgba(226,72,60,0.55)", ink: "#EFF6F7" },
  { field: "stoplight_yellow", color: "#F2D589", glow: "rgba(232,185,59,0.5)", ink: "#1A1405" },
  { field: "stoplight_green", color: "#3FAE6A", glow: "rgba(63,174,106,0.5)", ink: "#F1FFF6" },
];

function StoplightLegend({ slide }) {
  const lights = STOPLIGHTS.filter((l) => (slide[l.field] || "").trim());
  if (!lights.length) return null;
  return (
    <div className="mt-12 flex flex-wrap items-center justify-center gap-8 sm:gap-14" data-testid="slide-stoplight">
      {lights.map((l) => (
        <div
          key={l.field}
          data-testid={`stoplight-${l.field.replace("stoplight_", "")}`}
          className="flex items-center justify-center rounded-full text-center"
          style={{
            width: "clamp(150px, 15vw, 230px)",
            height: "clamp(150px, 15vw, 230px)",
            backgroundColor: l.color,
            color: l.ink,
            padding: "0 12%",
            fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
            fontSize: "clamp(16px, 1.6vw, 26px)",
            lineHeight: 1.2,
            fontWeight: 500,
            boxShadow: `0 0 60px ${l.glow}, inset 0 -14px 34px rgba(0,0,0,0.22), inset 0 12px 26px rgba(255,255,255,0.22)`,
          }}
        >
          {slide[l.field]}
        </div>
      ))}
    </div>
  );
}

/**
 * Renders a single presentation slide based on its `type`. The visual
 * language matches the rest of the Compose Yourself! Live experience:
 * deep-navy background, Frank Ruhl Libre serif headlines, warm gold
 * accents. Each slide type has its own layout but shares the progress
 * dots via <SlideShell/>.
 *
 * The `renderAppOverlay` prop is the escape hatch for the two
 * "app-embedded" slide types (Act 2 Scene 3, Act 3 Scene 2) — the
 * caller decides what to render for those (typically the full Live
 * Display staff + smoke + audience-submission takeover). Phase 1 just
 * shows a placeholder for those; Phase 4 will wire it up.
 */
function VolunteerNotes({ notes }) {
  const picked = (notes || []).filter(Boolean);
  if (!picked.length) return null;
  return (
    <div className="mt-12 flex flex-wrap items-center justify-center gap-6 sm:gap-10" data-testid="slide-volunteer-notes">
      {picked.map((n, i) => (
        <span
          key={`${n}-${i}`}
          data-testid={`volunteer-note-${i}`}
          style={{
            fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
            fontSize: "clamp(64px, 9vw, 150px)",
            lineHeight: 1,
            color: "#F2D589",
            textShadow: "0 0 48px rgba(232,196,138,0.35)",
          }}
        >
          {n}
        </span>
      ))}
    </div>
  );
}

function ShuffleCue({ src }) {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    if (!src) return undefined;
    // Browsers block audio until the page gets a gesture — Dan clicking
    // or hitting fullscreen on the display is enough.
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    let showTimer;
    let hideTimer;
    const schedule = () => {
      showTimer = setTimeout(() => {
        setVisible(true);
        playDing(0.7);
        hideTimer = setTimeout(() => {
          setVisible(false);
          schedule();
        }, 2000);
      }, 7000 + Math.random() * 8000);
    };
    schedule();
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [src]);

  if (!src) return null;
  // Fixed-height slot so the text above never reflows when the cue
  // pops in or out.
  return (
    <div
      className="mt-10 flex items-center justify-center pointer-events-none"
      style={{ height: "clamp(160px, 34vh, 420px)" }}
      data-testid="slide-shuffle-slot"
    >
      <AnimatePresence>
        {visible ? (
          <motion.img
            key="shuffle"
            src={src}
            alt="Shuffle"
            data-testid="slide-shuffle-image"
            initial={{ opacity: 0, scale: 0.55 }}
            animate={{ opacity: 1, scale: [0.55, 1.22, 1] }}
            exit={{ opacity: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } }}
            transition={{ duration: 0.42, ease: "easeOut" }}
            style={{
              maxHeight: "100%",
              maxWidth: "min(72vw, 900px)",
              objectFit: "contain",
              filter: "drop-shadow(0 18px 60px rgba(0,0,0,0.6))",
            }}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export function SlideRenderer({ slide, slides, currentSlideKey, renderAppOverlay, audienceUrl, slug, recapStartedAt, recapStepMs, volunteerNotes = [], hebrewDemo = null }) {
  if (!slide) return null;

  switch (slide.type) {
    case "title":
      return (
        <SlideShell slides={slides} currentSlideKey={currentSlideKey}>
          <div className="max-w-5xl flex flex-col items-center" data-testid="slide-title">
            {slide.logo_url ? (
              <img
                src={slide.logo_url}
                alt={slide.logo_alt || "Compose Yourself!"}
                className="w-full max-w-3xl h-auto"
                style={{ filter: "drop-shadow(0 8px 30px rgba(0,0,0,0.5))" }}
              />
            ) : (
              <h1
                className="text-6xl sm:text-7xl lg:text-8xl leading-tight"
                style={{
                  fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
                  fontWeight: 500,
                  color: "#F5EFE3",
                  letterSpacing: "-0.02em",
                }}
              >
                {slide.logo_text || "Compose Yourself!"}
              </h1>
            )}
            {slide.tagline ? (
              <p
                className="mt-10 text-xl sm:text-2xl"
                style={{ color: "#F2D589", letterSpacing: "0.16em", textTransform: "uppercase" }}
              >
                {slide.tagline}
              </p>
            ) : null}
          </div>
        </SlideShell>
      );

    case "intro":
      return (
        <SlideShell slides={slides} currentSlideKey={currentSlideKey}>
          <div className="max-w-5xl" data-testid="slide-intro">
            <div
              className="text-sm sm:text-base mb-6"
              style={{ color: "#F2D589", letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 600 }}
            >
              {slide.act_label}
            </div>
            <h2
              className="text-5xl sm:text-6xl lg:text-7xl leading-tight"
              style={{
                fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
                fontWeight: 500,
                color: "#F5EFE3",
              }}
            >
              {slide.headline}
            </h2>
            {slide.subhead ? (
              <p className="mt-8 text-xl sm:text-2xl max-w-3xl mx-auto" style={{ color: "rgba(245,239,227,0.72)" }}>
                {slide.subhead}
              </p>
            ) : null}
          </div>
        </SlideShell>
      );

    case "scene":
      return (
        <SlideShell slides={slides} currentSlideKey={currentSlideKey}>
          <div
            className={slide.scene_id === "act3-scene1" ? "w-full max-w-[1600px]" : "max-w-5xl"}
            data-testid={`slide-${slide.type}`}
          >
            <div
              className="text-sm sm:text-base mb-6"
              style={{ color: "#F2D589", letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 600 }}
            >
              {slide.scene_label}
            </div>
            <h2
              className="text-5xl sm:text-6xl lg:text-7xl leading-tight"
              style={{
                fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
                fontWeight: 500,
                color: "#F5EFE3",
              }}
            >
              {slide.headline}
            </h2>
            {slide.subhead ? (
              <p className="mt-8 text-xl sm:text-2xl max-w-3xl mx-auto" style={{ color: "rgba(245,239,227,0.72)" }}>
                {slide.subhead}
              </p>
            ) : null}
            <StoplightLegend slide={slide} />
            {slide.scene_id === "act1-scene3" ? <VolunteerNotes notes={volunteerNotes} /> : null}
            {slide.shuffle_image_url ? <ShuffleCue src={slide.shuffle_image_url} /> : null}
            {slide.scene_id === "act3-scene1" ? <HebrewDemoStaff demo={hebrewDemo} /> : null}
          </div>
        </SlideShell>
      );

    case "recap":
      // Act 3 Scene 3 — the live sticky-note collage. Progress dots
      // are hidden here because the collage frames the scene title
      // itself. Content auto-composes from /api/live/{slug}/recap.
      return (
        <SlideShell slides={slides} currentSlideKey={currentSlideKey} hideDots>
          <div className="absolute inset-0" data-testid="slide-recap">
            <RecapCollage
              slug={slug}
              slide={slide}
              startedAt={recapStartedAt}
              stepMs={recapStepMs}
            />
          </div>
        </SlideShell>
      );

    case "qr":
      // Act 2 Scene 3's first sub-slide — a big QR code + join
      // instructions. The QR points to the audience-submission URL
      // (`/live/{slug}`) so scanning it lands the audience on the
      // mobile submission form.
      return (
        <SlideShell slides={slides} currentSlideKey={currentSlideKey}>
          <div className="max-w-5xl flex flex-col items-center" data-testid="slide-qr">
            <div
              className="text-sm sm:text-base mb-4"
              style={{ color: "#FCA07A", letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 600 }}
            >
              {slide.scene_label}
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl leading-tight"
              style={{
                fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
                fontWeight: 500,
                color: "#F5EFE3",
              }}
            >
              {slide.headline}
            </h2>
            {slide.subhead ? (
              <p className="mt-4 text-lg sm:text-xl max-w-3xl" style={{ color: "rgba(245,239,227,0.72)" }}>
                {slide.subhead}
              </p>
            ) : null}
            {audienceUrl ? (
              <div
                className="mt-8 p-6 rounded-2xl"
                style={{ backgroundColor: "#F5EFE3", boxShadow: "0 30px 80px rgba(0,0,0,0.55)" }}
                data-testid="slide-qr-code"
              >
                <QRCodeSVG
                  value={audienceUrl}
                  size={320}
                  bgColor="#F5EFE3"
                  fgColor="#060312"
                  level="M"
                  includeMargin={false}
                />
              </div>
            ) : null}
            {audienceUrl ? (
              <div
                className="mt-5 text-lg sm:text-2xl"
                style={{
                  color: "#F5EFE3",
                  fontFamily: "'Courier Prime', monospace",
                  letterSpacing: "0.04em",
                }}
                data-testid="slide-qr-url"
              >
                {audienceUrl.replace(/^https?:\/\//, "")}
              </div>
            ) : null}
            <div
              className="mt-6 inline-block px-6 py-3 rounded-full"
              style={{
                backgroundColor: "rgba(252,160,122,0.14)",
                color: "#FCA07A",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {slide.qr_instructions || "Scan with your phone camera"}
            </div>
          </div>
        </SlideShell>
      );

    case "app":
      // Act 2 Scene 3 second sub-slide + Act 3 Scene 2. The classic
      // Fantasia Live experience takes over the screen underneath —
      // LiveDisplay handles this by NOT rendering the slide overlay
      // when the current slide.type is "app". If we ever DO render here
      // (e.g. an admin preview), show a passive breadcrumb.
      if (renderAppOverlay) return renderAppOverlay(slide);
      return null;

    case "conclusion":
      return (
        <SlideShell slides={slides} currentSlideKey={currentSlideKey}>
          <div className="max-w-5xl flex flex-col items-center" data-testid="slide-conclusion">
            <h2
              className="text-6xl sm:text-7xl lg:text-8xl leading-tight"
              style={{
                fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
                fontWeight: 500,
                color: "#F5EFE3",
              }}
            >
              {slide.headline}
            </h2>
            {slide.subhead ? (
              <p className="mt-6 text-xl sm:text-2xl max-w-3xl" style={{ color: "rgba(245,239,227,0.72)" }}>
                {slide.subhead}
              </p>
            ) : null}
            {audienceUrl ? (
              <div
                className="mt-8 p-6 rounded-2xl"
                style={{ backgroundColor: "#F5EFE3", boxShadow: "0 30px 80px rgba(0,0,0,0.55)" }}
                data-testid="slide-conclusion-qr"
              >
                <QRCodeSVG
                  value={audienceUrl}
                  size={280}
                  bgColor="#F5EFE3"
                  fgColor="#060312"
                  level="M"
                  includeMargin={false}
                />
              </div>
            ) : null}
            {audienceUrl ? (
              <div
                className="mt-5 text-lg sm:text-2xl"
                style={{
                  color: "#F5EFE3",
                  fontFamily: "'Courier Prime', monospace",
                  letterSpacing: "0.04em",
                }}
                data-testid="slide-conclusion-qr-url"
              >
                {audienceUrl.replace(/^https?:\/\//, "")}
              </div>
            ) : null}
          </div>
        </SlideShell>
      );

    default:
      return (
        <SlideShell slides={slides} currentSlideKey={currentSlideKey}>
          <div style={{ color: "#F5EFE3" }}>Unknown slide type: {slide.type}</div>
        </SlideShell>
      );
  }
}
